import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  useBillingPaymentSettingsForChannel,
  useChargeManager,
} from 'lane-shared/domains/billingPayments/hooks';
import styles from './index.scss';
import {
  ExternalEntityType,
  ExternalPayerType,
  Charge,
} from 'lane-shared/domains/billingPayments/types';
import { currencyToPaymentCurrency } from 'domains-billing-web-mappers';
import { Flex, Loading } from 'components';
import { UserType } from 'lane-shared/types/User';
import { toGraphQLContentType } from '../../../../helpers/contentTypeMapper';
import useUserLocale from 'hooks/useUserLocale';
import { ChargesList } from './ChargesList/ChargesList';
import { DraftChargeEditor } from './DraftChargeEditor/DraftChargeEditor';
import { InvoiceType } from 'graphql-query-contracts';
import { channelModuleByChannelId } from 'lane-shared/graphql/modules';
import { ContentTypeEnum } from 'constants-content';
import {
  getDefaultInvoiceType,
  getInvoicingOptionsEnabledValue,
  showChargeStatus,
} from '../../../workOrder/helpers/getInvoicingOptionsEnabledValue';
import { WorkOrderModuleSettings } from 'lane-shared/domains/workOrder';
import { useWorkOrdersServiceRequestInvoicingOptionsEnabled } from 'react-hooks';

type Props = {
  channel?: { _id: string };
  user: UserType;
  externalPayerId: string;
  externalPayerType: ExternalPayerType;
  externalEntityType: ExternalEntityType;
  externalEntityId?: string;
  invoiceType?: InvoiceType;
  chargeName: string;
  chargeDescription?: string;
  onSubmitCharge?: (charge: Charge) => void;
  metadata?: Record<string, any>;
};
export function ChargeManager({
  channel,
  user,
  externalPayerId,
  externalPayerType = ExternalPayerType.EXTERNAL_PAYER_TYPE_ACTIVATE_USER,
  externalEntityType = ExternalEntityType.EXTERNAL_ENTITY_TYPE_ACTIVATE_INVOICE,
  externalEntityId,
  chargeName,
  chargeDescription,
  onSubmitCharge,
  metadata,
}: Props) {
  const { currency, invoiceDueInDays, isInvoicingDisabled } =
    useBillingPaymentSettingsForChannel({
      channelId: channel?._id,
    });
  const locale = useUserLocale();

  const { data: moduleResp } = useQuery(channelModuleByChannelId, {
    variables: {
      channelId: channel?._id,
      contentType: toGraphQLContentType(ContentTypeEnum.WorkOrder),
    },
  });
  const settings: WorkOrderModuleSettings =
    moduleResp?.channelModuleByChannelId?.settings;

  const isWorkOrdersServiceRequestInvoicingOptionsEnabled =
    useWorkOrdersServiceRequestInvoicingOptionsEnabled();

  const [newInvoiceDueDate, setNewInvoiceDueDate] = useState<Date | undefined>(
    undefined
  );

  const [newInvoiceType, setNewInvoiceType] = useState<InvoiceType>(
    InvoiceType.InvoiceTypeOneoff
  );

  useEffect(() => {
    const defaultInvoiceType = getDefaultInvoiceType(
      isInvoicingDisabled,
      settings?.isInvoiceOptionsEnabled
    );

    if (
      isWorkOrdersServiceRequestInvoicingOptionsEnabled &&
      defaultInvoiceType !== InvoiceType.InvoiceTypeNoInvoice
    ) {
      setNewInvoiceType(InvoiceType.InvoiceTypeMonthly);
    } else {
      setNewInvoiceType(defaultInvoiceType);
    }
  }, [
    isWorkOrdersServiceRequestInvoicingOptionsEnabled,
    isInvoicingDisabled,
    settings?.isInvoiceOptionsEnabled,
  ]);

  let showSRChargeStatus = true;

  if (isWorkOrdersServiceRequestInvoicingOptionsEnabled) {
    showSRChargeStatus = showChargeStatus(
      isInvoicingDisabled,
      settings?.isInvoiceOptionsEnabled
    );
  }

  const {
    charges,
    draftCharge,
    loading,
    validation,
    submitDraftCharge,
    voidCharge,
    addDraftProductItem,
    updateDraftProductItem,
    deleteDraftProductItem,
  } = useChargeManager({
    channel,
    actingUser: user,
    externalPayerId,
    externalPayerType,
    currency,
    externalEntityId,
    externalEntityType,
    invoiceType: newInvoiceType,
    locale,
    chargeName,
    chargeDescription,
    invoiceDueInDays: invoiceDueInDays ?? 30,
    onSubmitCharge,
    metadata,
  });

  const updateChargeDetails = (
    value: InvoiceType | Date,
    fieldname: string
  ) => {
    if (fieldname === 'invoiceType' && typeof value === 'string') {
      setNewInvoiceType(value);
    }

    if (fieldname === 'invoiceDueDate' && value instanceof Date) {
      setNewInvoiceDueDate(value);
    }
  };

  const handleSubmitCharge = async () => {
    await submitDraftCharge(newInvoiceType, newInvoiceDueDate);
  };

  if (loading) {
    return (
      <Flex p={4} width="full" justify="center" align="center">
        <Loading />
      </Flex>
    );
  }

  const paymentCurrency = currencyToPaymentCurrency(currency);

  const isInvoiceOptionEnabled =
    isWorkOrdersServiceRequestInvoicingOptionsEnabled &&
    getInvoicingOptionsEnabledValue(
      isInvoicingDisabled,
      settings?.isInvoiceOptionsEnabled
    );

  return (
    <Flex gap={4} className={styles.ChargeManager} direction="column">
      <DraftChargeEditor
        items={draftCharge.items}
        showEmpty={charges.length > 0}
        locale={locale}
        currency={paymentCurrency}
        validation={validation}
        payerId={externalPayerId}
        payerType={externalPayerType}
        handleSubmitDraftCharge={handleSubmitCharge}
        addDraftProductItem={addDraftProductItem}
        updateDraftProductItem={updateDraftProductItem}
        deleteDraftProductItem={deleteDraftProductItem}
        updateChargeDetails={updateChargeDetails}
        isInvoiceOptionEnabled={isInvoiceOptionEnabled}
        invoiceType={newInvoiceType}
        channelId={channel?._id}
      />
      {charges.length > 0 && (
        <ChargesList
          charges={charges}
          currency={paymentCurrency}
          locale={locale}
          voidCharge={voidCharge}
          showSRChargeStatus={showSRChargeStatus}
        />
      )}
    </Flex>
  );
}
